import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { useState } from "react";

import {
  buildApplyToAllPayload,
  buildOrganisationAccessPayload,
  collectKnownOrganisationIds,
  formatFeatureAccessStatusLabel,
  formatOrganisationAccessLabel,
  isEntitlementRecordActive,
  isGlobalEnabled,
  resolveFeatureAccessStatus,
} from "./featureAccessConsoleHelpers";
import {
  featureAccessEnvironmentLabel,
  formatEnvironmentUnavailabilityHint,
  resolveFeatureAccessEnvironmentUnavailability,
} from "./featureAccessEnvironments";
import { FeatureAccessOrganisationPicker } from "./FeatureAccessOrganisationPicker";
import type { FeatureAccessDefinition } from "./featureAccessRegistry";
import type {
  FeatureConfirmRequest,
  FeatureConsoleFeedback,
} from "./FeatureAccessRow";

import {
  useDeleteAdminV2EntitlementMutation,
  useGetAdminV2FeatureAccessQuery,
  useUpsertAdminV2EntitlementMutation,
} from "@/store/api/baseApi";
import type { AdminV2FeatureAccessEnvironment } from "@/types/adminV2";
import { VNextButton, VNextNotice, VNextSwitch } from "@/vnext/primitives";
import { extractPublicMutationError } from "@/vnext/routes/settings/extractPublicMutationError";

interface FeatureEnvironmentCommonProps {
  feature: FeatureAccessDefinition;
  environment: AdminV2FeatureAccessEnvironment;
  canManage: boolean;
  /** Whether the feature is currently shown to normal admins. */
  ready: boolean;
  onFeedback: (feedback: FeatureConsoleFeedback) => void;
  onBlockedNotReady: (featureLabel: string) => void;
}

/**
 * The "On for all organisations" cell for one feature on one site.
 *
 * @remarks
 * Shares its summary query with {@link FeatureAccessEnvironmentPanel} (same
 * query key, so RTK Query serves both from one request). When the API refuses
 * the environment — the staging admin asking about production, or production
 * without a configured staging gateway — the cell renders an explanatory hint
 * instead of a switch.
 */
export const FeatureEnvironmentToggleCell = ({
  feature,
  environment,
  canManage,
  ready,
  onFeedback,
  onRequestConfirm,
  onBlockedNotReady,
}: FeatureEnvironmentCommonProps & {
  onRequestConfirm: (request: FeatureConfirmRequest) => void;
}) => {
  const [now] = useState(() => Date.now());
  const environmentLabel = featureAccessEnvironmentLabel(environment);
  const summaryQuery = useGetAdminV2FeatureAccessQuery({
    entitlementKey: feature.entitlementKey,
    environment,
  });
  const [upsertEntitlement, upsertState] =
    useUpsertAdminV2EntitlementMutation();

  const globalEnabled = isGlobalEnabled(summaryQuery.data, now);
  const status = resolveFeatureAccessStatus(summaryQuery.data, now);
  const unavailability = summaryQuery.isError
    ? resolveFeatureAccessEnvironmentUnavailability(summaryQuery.error)
    : null;

  const requestApplyToAll = (enable: boolean) => {
    if (!ready) {
      onBlockedNotReady(feature.label);
      return;
    }
    onRequestConfirm({
      title: `Apply to all organisations on ${environmentLabel}`,
      message: enable
        ? `Turn on ${feature.label} for every organisation on ${environmentLabel}? Organisations you have individually excluded will stay off.`
        : `Turn off the ${environmentLabel} global switch for ${feature.label}? Organisations you have turned on individually keep their access until you remove them.`,
      confirmLabel: enable ? "Turn on for all" : "Turn off for all",
      onConfirm: () => {
        void (async () => {
          try {
            await upsertEntitlement(
              buildApplyToAllPayload(
                feature.entitlementKey,
                environment,
                enable,
              ),
            ).unwrap();
            await summaryQuery.refetch();
            onFeedback({
              tone: "info",
              details: {
                message: enable
                  ? `${feature.label} is now on for all organisations on ${environmentLabel}.`
                  : `${feature.label} is now off for all organisations on ${environmentLabel}.`,
              },
            });
          } catch (error) {
            onFeedback({
              tone: "danger",
              details: extractPublicMutationError(
                error,
                "The change could not be saved.",
              ),
            });
          }
        })();
      },
    });
  };

  if (summaryQuery.isLoading) {
    return (
      <td>
        <span className="vnext-feature-row__hint">Loading…</span>
      </td>
    );
  }

  if (summaryQuery.isError) {
    return (
      <td>
        <span className="vnext-feature-row__hint">
          {unavailability
            ? formatEnvironmentUnavailabilityHint(unavailability)
            : "Unavailable"}
        </span>
      </td>
    );
  }

  return (
    <td>
      <VNextSwitch
        checked={globalEnabled}
        disabled={!canManage || upsertState.isLoading}
        label={`Turn ${feature.label} on for everyone on ${environmentLabel}`}
        onChange={() => requestApplyToAll(!globalEnabled)}
      />
      <div className="vnext-feature-row__hint">
        {ready
          ? formatFeatureAccessStatusLabel(status)
          : "Not ready for rollout"}
      </div>
    </td>
  );
};

/**
 * The expanded per-organisation access list for one feature on one site: the
 * organisation picker, the bulk on/off bar, and the override table.
 *
 * @remarks
 * This is the per-environment half of the old single-environment row detail.
 * All working state (staged organisations, tick selection, bulk busy flag) is
 * scoped to the panel, so the two sites' lists are completely independent —
 * which is the point of the per-site console.
 */
export const FeatureAccessEnvironmentPanel = ({
  feature,
  environment,
  canManage,
  ready,
  onFeedback,
  onBlockedNotReady,
}: FeatureEnvironmentCommonProps) => {
  // Captured once per mount via a lazy initializer (keeps render pure); only
  // gates active/expiry windows where minute staleness is irrelevant.
  const [now] = useState(() => Date.now());
  const environmentLabel = featureAccessEnvironmentLabel(environment);
  const summaryQuery = useGetAdminV2FeatureAccessQuery({
    entitlementKey: feature.entitlementKey,
    environment,
  });
  const [upsertEntitlement, upsertState] =
    useUpsertAdminV2EntitlementMutation();
  const [deleteEntitlement, deleteState] =
    useDeleteAdminV2EntitlementMutation();
  // Bulk org-access state: ids ticked in the table, orgs staged from the search
  // (shown in the table but not yet written), and a busy flag for the looped
  // bulk save.
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [stagedOrgs, setStagedOrgs] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const summary = summaryQuery.data;
  const globalEnabled = isGlobalEnabled(summary, now);
  const knownOrganisationIds = collectKnownOrganisationIds(summary);
  const organisations = summary?.organisations ?? [];

  // Table rows = existing override rows + orgs staged from the search. Staged
  // orgs that already have a row are dropped to avoid duplicates after refetch.
  const stagedRows = stagedOrgs.filter(
    (staged) => !organisations.some((org) => org.organisationId === staged.id),
  );
  const orgRows = [
    ...organisations.map((org) => ({
      id: org.organisationId,
      name: org.organisationName ?? org.organisationId,
      record: org.record,
    })),
    ...stagedRows.map((staged) => ({
      id: staged.id,
      name: staged.name,
      record: null,
    })),
  ];
  // Hide existing and staged orgs from the search so none can be added twice.
  const pickerKnownOrganisationIds = new Set(knownOrganisationIds);
  for (const staged of stagedOrgs) {
    pickerKnownOrganisationIds.add(staged.id);
  }
  const selectedCount = selectedOrgIds.size;
  const allOrgRowsSelected =
    orgRows.length > 0 && orgRows.every((row) => selectedOrgIds.has(row.id));

  const isBusy = upsertState.isLoading || deleteState.isLoading;
  const actionsDisabled = !canManage || isBusy;
  const bulkDisabled = actionsDisabled || bulkBusy;

  const unavailability = summaryQuery.isError
    ? resolveFeatureAccessEnvironmentUnavailability(summaryQuery.error)
    : null;

  /**
   * Runs a rollout action, or diverts to the "not ready" modal when the
   * feature is not ready for admins. Every rollout write goes through here.
   */
  const runRolloutAction = (action: () => void): void => {
    if (!ready) {
      onBlockedNotReady(feature.label);
      return;
    }
    action();
  };

  const setOrganisationAccess = async (
    organisationId: string,
    organisationName: string | null,
    enable: boolean,
  ) => {
    const name = organisationName ?? "organisation";
    try {
      await upsertEntitlement(
        buildOrganisationAccessPayload(
          feature.entitlementKey,
          environment,
          organisationId,
          enable,
        ),
      ).unwrap();
      await summaryQuery.refetch();
      onFeedback({
        tone: "info",
        details: {
          message: enable
            ? `${feature.label} turned on for ${name} on ${environmentLabel}.`
            : `${feature.label} turned off for ${name} on ${environmentLabel}.`,
        },
      });
    } catch (error) {
      onFeedback({
        tone: "danger",
        details: extractPublicMutationError(
          error,
          "The change could not be saved.",
        ),
      });
    }
  };

  const toggleSelectAll = () => {
    setSelectedOrgIds(
      allOrgRowsSelected ? new Set() : new Set(orgRows.map((row) => row.id)),
    );
  };

  const toggleSelectOne = (organisationId: string) => {
    setSelectedOrgIds((current) => {
      const next = new Set(current);
      if (next.has(organisationId)) {
        next.delete(organisationId);
      } else {
        next.add(organisationId);
      }
      return next;
    });
  };

  /** Add a searched org to the table (staged, not written) and tick it. */
  const stageOrganisation = (organisation: { id: string; name: string }) => {
    setStagedOrgs((current) =>
      current.some((staged) => staged.id === organisation.id)
        ? current
        : [...current, organisation],
    );
    setSelectedOrgIds((current) => {
      const next = new Set(current);
      next.add(organisation.id);
      return next;
    });
  };

  const orgWord = (count: number) =>
    `${count} organisation${count === 1 ? "" : "s"}`;

  /**
   * Write access for a set of organisations in one pass. The entitlements API
   * has no batch endpoint, so this loops per-org upserts and reports a single
   * summary; staged rows become real rows on the refetch. Serves both the
   * bulk bar ("Turn on/off selected") and the picker's exclude mode.
   */
  const runBulkAccessForTargets = async (
    targets: Array<{ id: string; name: string }>,
    enable: boolean,
    formatSuccess: (savedCount: number) => string,
  ) => {
    if (targets.length === 0) {
      return;
    }
    setBulkBusy(true);
    let savedCount = 0;
    let failedCount = 0;
    for (const target of targets) {
      try {
        await upsertEntitlement(
          buildOrganisationAccessPayload(
            feature.entitlementKey,
            environment,
            target.id,
            enable,
          ),
        ).unwrap();
        savedCount += 1;
      } catch {
        failedCount += 1;
      }
    }
    await summaryQuery.refetch();
    // The written targets are now real rows (or failed); drop them from the
    // staged working set and selection, keeping any untouched staged orgs.
    const targetIds = new Set(targets.map((target) => target.id));
    setStagedOrgs((current) =>
      current.filter((staged) => !targetIds.has(staged.id)),
    );
    setSelectedOrgIds((current) => {
      const next = new Set(current);
      for (const id of targetIds) {
        next.delete(id);
      }
      return next;
    });
    setBulkBusy(false);
    if (failedCount === 0) {
      onFeedback({
        tone: "info",
        details: { message: formatSuccess(savedCount) },
      });
    } else {
      onFeedback({
        tone: "danger",
        details: {
          message: `${feature.label} (${environmentLabel}): updated ${orgWord(
            savedCount,
          )}, but ${orgWord(failedCount)} could not be saved.`,
        },
      });
    }
  };

  const applyBulkAccess = (enable: boolean) => {
    runRolloutAction(
      () =>
        void runBulkAccessForTargets(
          orgRows.filter((row) => selectedOrgIds.has(row.id)),
          enable,
          (savedCount) =>
            `${feature.label} turned ${enable ? "on" : "off"} for ${orgWord(
              savedCount,
            )} on ${environmentLabel}.`,
        ),
    );
  };

  /**
   * Exclude organisations while the feature is on for everyone: writes each
   * org row off immediately (an org row overrides the global row), so the
   * picker's "Exclude" action is one step — no staged rows to apply.
   */
  const excludeOrganisations = (
    organisations: Array<{ id: string; name: string }>,
  ) => {
    void runBulkAccessForTargets(
      organisations,
      false,
      (savedCount) =>
        `Excluded ${orgWord(savedCount)} from ${
          feature.label
        } on ${environmentLabel} — still on for everyone else.`,
    );
  };

  /**
   * Remove an organisation from the table. A staged (unsaved) row just leaves
   * the working set; an existing override is deleted server-side so the org
   * reverts to the feature default. The bin is only offered when the org is off
   * (see the render), so a live org is never silently revoked.
   */
  const removeOrganisation = async (row: {
    id: string;
    name: string;
    record: { isEnabled: boolean } | null;
  }) => {
    // Drop the org from the local working set. Crucially this clears any
    // stale staged copy too: an org added via search lingers in `stagedOrgs`
    // even after it is saved, and `stagedRows` only hides it while it is still
    // in the summary — so without this it would reappear as a Pending row the
    // moment the server delete removes it from the summary.
    const dropFromWorkingSet = () => {
      setStagedOrgs((current) =>
        current.filter((staged) => staged.id !== row.id),
      );
      setSelectedOrgIds((current) => {
        const next = new Set(current);
        next.delete(row.id);
        return next;
      });
    };

    if (!row.record) {
      dropFromWorkingSet();
      return;
    }

    try {
      await deleteEntitlement({
        entitlementKey: feature.entitlementKey,
        organisationId: row.id,
        environment,
      }).unwrap();
      await summaryQuery.refetch();
      dropFromWorkingSet();
      onFeedback({
        tone: "info",
        details: {
          message: `${row.name} removed from ${feature.label} on ${environmentLabel}.`,
        },
      });
    } catch (error) {
      onFeedback({
        tone: "danger",
        details: extractPublicMutationError(
          error,
          "The organisation could not be removed.",
        ),
      });
    }
  };

  if (summaryQuery.isError) {
    return (
      <VNextNotice tone={unavailability ? "warning" : "danger"}>
        {unavailability
          ? `${environmentLabel} feature access is not available here: ${formatEnvironmentUnavailabilityHint(
              unavailability,
            ).toLowerCase()}.`
          : `${environmentLabel} feature access could not be loaded.`}
      </VNextNotice>
    );
  }

  return (
    <>
      {!ready ? (
        <div className="vnext-feature-row__not-ready">
          This feature is not ready for release yet, so it cannot be turned on
          for organisations. Check in with the dev team if it should go live.
        </div>
      ) : null}

      <FeatureAccessOrganisationPicker
        actionLabel={globalEnabled ? "Exclude" : "Add"}
        disabled={bulkDisabled}
        environment={environment}
        environmentLabel={environmentLabel}
        hint={
          ready
            ? globalEnabled
              ? `This feature is on for everyone on ${environmentLabel}. Exclude an organisation to turn it off just for them.`
              : `This feature is off by default on ${environmentLabel}. Add organisations to turn it on for them.`
            : undefined
        }
        knownOrganisationIds={pickerKnownOrganisationIds}
        onSelect={(organisations) =>
          runRolloutAction(() => {
            if (globalEnabled) {
              excludeOrganisations(organisations);
              return;
            }
            for (const organisation of organisations) {
              stageOrganisation(organisation);
            }
          })
        }
      />

      {orgRows.length === 0 ? (
        <div className="vnext-empty">
          {globalEnabled
            ? `No organisations excluded — everyone on ${environmentLabel} has this feature.`
            : "No organisations added yet. Search above to add some."}
        </div>
      ) : (
        <>
          {selectedCount > 0 ? (
            <div className="vnext-feature-row__bulk-bar">
              <span className="vnext-feature-row__bulk-count">
                {selectedCount} selected
              </span>
              <VNextButton
                disabled={bulkDisabled}
                onClick={() => applyBulkAccess(true)}
                tone="primary"
              >
                Turn on selected
              </VNextButton>
              <VNextButton
                disabled={bulkDisabled}
                onClick={() => applyBulkAccess(false)}
                tone="secondary"
              >
                Turn off selected
              </VNextButton>
            </div>
          ) : null}
          <div className="vnext-scroll-panel">
            <table className="vnext-table">
              <thead>
                <tr>
                  <th className="vnext-feature-row__select-cell">
                    <input
                      aria-label={`Select all ${environmentLabel} organisations`}
                      checked={allOrgRowsSelected}
                      className="vnext-feature-row__checkbox"
                      disabled={bulkDisabled}
                      onChange={toggleSelectAll}
                      type="checkbox"
                    />
                  </th>
                  <th>Organisation</th>
                  <th>Access</th>
                  <th className="vnext-feature-row__select-cell">
                    <span className="vnext-sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orgRows.map((row) => {
                  const orgIsOn =
                    row.record !== null &&
                    isEntitlementRecordActive(row.record, now);
                  return (
                    <tr key={row.id}>
                      <td className="vnext-feature-row__select-cell">
                        <input
                          aria-label={`Select ${row.name}`}
                          checked={selectedOrgIds.has(row.id)}
                          className="vnext-feature-row__checkbox"
                          disabled={bulkDisabled}
                          onChange={() => toggleSelectOne(row.id)}
                          type="checkbox"
                        />
                      </td>
                      <td>{row.name}</td>
                      <td>
                        <div className="vnext-feature-row__org-access">
                          <VNextSwitch
                            checked={row.record ? row.record.isEnabled : false}
                            disabled={bulkDisabled}
                            label={`Access to ${feature.label} for ${row.name} on ${environmentLabel}`}
                            onChange={(next) =>
                              runRolloutAction(
                                () =>
                                  void setOrganisationAccess(
                                    row.id,
                                    row.name,
                                    next,
                                  ),
                              )
                            }
                          />
                          <span className="vnext-feature-row__hint">
                            {row.record
                              ? formatOrganisationAccessLabel(
                                  row.record,
                                  globalEnabled,
                                  now,
                                )
                              : "Off"}
                          </span>
                        </div>
                      </td>
                      <td className="vnext-feature-row__select-cell">
                        <button
                          aria-label={
                            orgIsOn
                              ? `Turn ${row.name} off before removing`
                              : `Remove ${row.name}`
                          }
                          className="vnext-feature-row__remove"
                          disabled={bulkDisabled || orgIsOn}
                          onClick={() => void removeOrganisation(row)}
                          title={
                            orgIsOn
                              ? "Turn off first to remove"
                              : "Remove from the table"
                          }
                          type="button"
                        >
                          <DeleteOutlineIcon
                            aria-hidden="true"
                            fontSize="small"
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};
